'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import { SITE_NAME } from '@/lib/site-config';

const sections = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `${SITE_NAME} ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. By using ${SITE_NAME}, you consent to the practices described in this policy.`,
  },
  {
    id: 'data-collected',
    title: '2. Information We Collect',
    content: `We collect: (a) Account information — name, email address, phone number, and profile details you provide during registration; (b) Identity verification data — PAN card, Aadhaar (last 4 digits), and bank/UPI details for payout processing; (c) Usage data — pages visited, features used, time spent, and device/browser information; (d) Review content — product reviews, ratings, and photos you submit; (e) Communication data — messages sent through our contact forms or support channels; (f) Payment data — transaction history, withdrawal requests, and payout records.`,
  },
  {
    id: 'how-we-use',
    title: '3. How We Use Your Information',
    content: `We use your information to: (a) operate and improve the Platform; (b) process reviewer payouts via UPI or bank transfer; (c) verify reviewer and brand identities to prevent fraud; (d) send transactional emails (campaign updates, payout confirmations); (e) send marketing communications (with your consent); (f) comply with legal obligations including tax reporting; (g) analyse platform usage to improve user experience; (h) detect and prevent fraud, abuse, and policy violations.`,
  },
  {
    id: 'data-sharing',
    title: '4. Information Sharing',
    content: `We do not sell your personal data. We may share your information with: (a) Brands/sellers — only your reviewer profile (name, category interests, review count) is visible to brands running campaigns you apply to; (b) Payment processors — UPI and banking partners for payout processing; (c) Service providers — cloud hosting, email delivery, and analytics providers bound by confidentiality agreements; (d) Legal authorities — when required by law, court order, or to protect our rights; (e) Business transfers — in the event of a merger or acquisition, with appropriate notice to users.`,
  },
  {
    id: 'data-security',
    title: '5. Data Security',
    content: `We implement industry-standard security measures including SSL/TLS encryption, secure database access controls, and regular security audits. Sensitive data such as bank account numbers and PAN details are encrypted at rest. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security. We encourage you to use a strong password and not share your account credentials.`,
  },
  {
    id: 'cookies',
    title: '6. Cookies & Tracking',
    content: `We use cookies and similar technologies to maintain your session, remember preferences, and analyse usage patterns. We use Google Analytics to understand how users interact with the Platform. You can control cookie settings through your browser. Disabling cookies may affect some Platform functionality. We do not use cookies for cross-site advertising tracking.`,
  },
  {
    id: 'retention',
    title: '7. Data Retention',
    content: `We retain your account data for as long as your account is active. If you delete your account, we will delete or anonymise your personal data within 30 days, except where retention is required by law (e.g., financial records for 7 years as per Indian tax law). Review content may be retained in anonymised form for platform quality purposes.`,
  },
  {
    id: 'rights',
    title: '8. Your Rights',
    content: `Under applicable Indian data protection laws, you have the right to: (a) access the personal data we hold about you; (b) correct inaccurate data; (c) request deletion of your data (subject to legal retention requirements); (d) withdraw consent for marketing communications at any time; (e) lodge a complaint with the relevant data protection authority. To exercise these rights, contact us at privacy@indianreviewclub.com.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: `The Platform is not intended for users under 18 years of age. We do not knowingly collect personal information from minors. If we become aware that a minor has provided us with personal information, we will delete it promptly. Parents or guardians who believe their child has provided us with information should contact us immediately.`,
  },
  {
    id: 'third-party',
    title: '10. Third-Party Links',
    content: `The Platform may contain links to third-party websites (e.g., Amazon, Flipkart product pages). We are not responsible for the privacy practices of these sites. We encourage you to review the privacy policies of any third-party sites you visit.`,
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. We will notify you of significant changes via email or a prominent notice on the Platform. Your continued use of the Platform after changes constitutes acceptance of the updated policy. The "Last updated" date at the top of this page indicates when the policy was last revised.`,
  },
  {
    id: 'contact',
    title: '12. Contact Us',
    content: `For privacy-related queries, data access requests, or to report a concern, contact our Data Protection Officer at privacy@indianreviewclub.com or write to us at: IndianReviewClub, Bangalore, Karnataka, India.`,
  },
];

export default function PrivacyPage() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        <section className="bg-[#0F0F1A] text-white py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/70">Legal</span>
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight">
              Privacy Policy
            </h1>
            <p className="text-white/50 text-base max-w-2xl leading-relaxed">
              Your privacy matters to us. This policy explains what data we collect, why we collect it, and how we protect it.
            </p>
            <p className="text-white/30 text-sm mt-4">Last updated: August 2026</p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 shrink-0">
              <div className="sticky top-24">
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">Contents</p>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#${s?.id}`}
                      className="block text-sm text-gray-500 hover:text-primary transition-colors py-1 border-l-2 border-transparent hover:border-primary pl-3"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <article className="flex-1 min-w-0 space-y-10">
              {sections?.map((s) => (
                <div key={s?.id} id={s?.id} className="scroll-mt-24">
                  <h2 className="font-heading font-bold text-xl text-gray-900 mb-3">{s?.title}</h2>
                  <p className="text-gray-600 leading-relaxed text-[15px]">{s?.content}</p>
                </div>
              ))}

              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-6 mt-8">
                <p className="text-sm font-semibold text-gray-800 mb-1">Privacy concerns?</p>
                <p className="text-sm text-gray-500">
                  Email our Data Protection Officer at{' '}
                  <a href="mailto:privacy@indianreviewclub.com" className="text-primary hover:underline">
                    privacy@indianreviewclub.com
                  </a>
                </p>
              </div>
            </article>
          </div>
        </div>
      </main>
      <Footer />
      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
